import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { keys } from 'lodash-es';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

import { CountryCodes } from '@shared/components';
import { AccountBalance, FinancialAccountBalances } from '@shared/models';
import { AggregatedAccountBalanceActions, fromAggregatedAccountBalance, fromAuth } from '@shared/store';
import { ErrorUtils } from '@shared/utils';

import { FormSelectFieldComponent } from '../form-select-field/form-select-field.component';

@Component({
  selector: 'app-currency-select',
  templateUrl: './currency-select.component.html',
  styleUrls: ['./currency-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormSelectFieldComponent],
})
export class CurrencySelectComponent implements OnInit {
  isLoading = true;

  selectedCurrency!: string;

  businessAccountCountryCode$ = this.store.select(fromAuth.selectBusinessAccountCountryCode);

  aggregateAccountBalance$ = this.store.select(fromAggregatedAccountBalance.selectAggregatedAccountBalanceDetails);

  businessAccountCurrencyList: { label: string; value: string }[] = [];

  currencyList: string[] = [];

  accountBalanceList!: FinancialAccountBalances | null;

  @Output() accountBalanceValues = new EventEmitter<AccountBalance>();

  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  ngOnInit() {
    this.store.dispatch(AggregatedAccountBalanceActions.initialize());

    // Get country code once to set initial currency
    this.businessAccountCountryCode$.pipe(take(1)).subscribe((countryCode) => {
      if (!this.selectedCurrency) {
        this.selectedCurrency = CountryCodes[countryCode] ?? 'USD';
        this.store.dispatch(AggregatedAccountBalanceActions.setCurrency({ currency: this.selectedCurrency }));
      }
    });

    // Subscribe to account balances to build currency list (fiat only)
    this.aggregateAccountBalance$
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (accountBalances) => {
          this.accountBalanceList = accountBalances;

          if (this.accountBalanceList && this.accountBalanceList[this.selectedCurrency]) {
            this.store.dispatch(
              AggregatedAccountBalanceActions.setAggregateAccountBalance({
                aggregateAccountBalance: this.accountBalanceList[this.selectedCurrency],
              }),
            );
          }

          // Reset currency lists before rebuilding
          const tempCurrencyList: string[] = [];
          const tempBusinessAccountCurrencyList: { label: string; value: string }[] = [];

          // Currencies to exclude from dropdown
          const excludedCurrencies = ['MWK', 'MGA', 'MAD'];

          // Add only fiat currencies from account balances (excluding unsupported ones)
          keys(accountBalances).forEach((key) => {
            if (!tempCurrencyList.includes(key) && !excludedCurrencies.includes(key)) {
              tempBusinessAccountCurrencyList.push({ label: key, value: key });
              tempCurrencyList.push(key);
            }
          });

          this.currencyList = tempCurrencyList;
          this.businessAccountCurrencyList = tempBusinessAccountCurrencyList;

          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          ErrorUtils.catchError('financialAccountService.accountCurrency', error);
        },
      });
  }

  onCurrencySelect(currency: string) {
    this.selectedCurrency = currency;
    this.store.dispatch(AggregatedAccountBalanceActions.setCurrency({ currency }));
    if (this.accountBalanceList && this.accountBalanceList[this.selectedCurrency]) {
      this.store.dispatch(
        AggregatedAccountBalanceActions.setAggregateAccountBalance({
          aggregateAccountBalance: this.accountBalanceList[this.selectedCurrency],
        }),
      );
    }
  }
}
